import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../UI/Button";

const CreatorCard = ({ media, poster, alt, buttonText, linkTo = "/templates", isVideo = false, animationDelay = 0 }) => {
  const videoRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (isVideo && videoRef.current) {
      videoRef.current.muted = false;
      const playPromise = videoRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // Auto-play was prevented, keep video muted and try again
          console.log("Playback with sound prevented, playing muted:", error);
          videoRef.current.muted = true;
          videoRef.current.play().catch(e => console.log("Muted playback also failed:", e));
        });
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (isVideo && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.muted = true;
      videoRef.current.load(); // This resets the video and shows the poster
    }
  };

  return (
    <Link to={linkTo} className="block">
      <div
        className="relative flex flex-col items-center group cursor-pointer transform transition-all duration-500 hover:scale-[1.02]"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ transitionDelay: `${animationDelay}ms` }}
      >
        {/* Media container with ag-green stroke offset - using padding-bottom for aspect ratio */}
        <div className="relative w-full overflow-hidden rounded-[15px] sm:rounded-[20px] md:rounded-[26px] lg:rounded-[32px] xl:rounded-[38px] 2xl:rounded-[42px] transition-all duration-500 group-hover:shadow-[0_0_8px_rgba(198,233,130,0.4)] sm:group-hover:shadow-[0_0_12px_rgba(198,233,130,0.4)] md:group-hover:shadow-[0_0_15px_rgba(198,233,130,0.4)] lg:group-hover:shadow-[0_0_18px_rgba(198,233,130,0.4)] group-hover:ring-1 sm:group-hover:ring-2 group-hover:ring-ag-green/40" style={{ paddingBottom: '56.25%' }}>
          {/* Dimming overlay - only visible when not hovered and is video */}
          {isVideo && !isHovered && (
            <div className="absolute inset-0 bg-black/50 z-10 transition-opacity duration-300"></div>
          )}
          
          {/* Main media - fills the container */}
          {isVideo ? (
            <video
              ref={videoRef}
              src={media}
              poster={poster}
              alt={alt}
              className="absolute inset-0 w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              src={media}
              alt={alt}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          
          {/* AG-green stroke overlay - offset inside (2% from each edge) - only visible on hover */}
          <div className={`absolute inset-0 pointer-events-none transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute top-[0.2%] left-[0.2%] right-[0.2%] bottom-[0.2%] border-[4px] sm:border-[5px] md:border-[7px] lg:border-[9px] xl:border-[10px] border-ag-green rounded-[15px] sm:rounded-[20px] md:rounded-[26px] lg:rounded-[32px] xl:rounded-[38px] 2xl:rounded-[42px] blur-sm"></div>
          </div>
        </div>

        {/* Button positioned at bottom center */}
        <div className="absolute bottom-2 sm:bottom-3 md:bottom-4 lg:bottom-5 xl:bottom-6 2xl:bottom-7 left-1/2 transform -translate-x-1/2 z-20">
          <div className="px-0.5 sm:px-1 py-0.5 sm:py-1 bg-ag-black-600 rounded sm:rounded-md group-hover:bg-ag-black-800 border border-ag-black-400 transition-colors duration-300">
            <Button
              className="w-full sm:w-auto text-[9px] sm:text-[10px] md:text-xs font-sourceSans tracking-tighter font-bold pointer-events-none"
              variant="special"
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CreatorCard;